// Notification Settings Component
import React from 'react';
import { Bell, BellOff, AlertCircle, Check } from 'lucide-react';
import { useDesktopNotifications } from '@/hooks/useDesktopNotifications';
import { PushNotificationService } from '@/services/pushNotifications';
import { useNotifications } from '@/components/NotificationProvider';

export default function NotificationSettings() {
  const {
    isSupported,
    permission,
    isEnabled,
    canNotify,
    requestPermission,
    toggleNotifications,
    notify
  } = useDesktopNotifications();
  
  const { showNotification } = useNotifications();

  const handleEnableNotifications = async () => {
    if (permission !== 'granted') {
      const result = await requestPermission();
      if (result === 'granted') {
        // Show test notification
        notify.message('WHIPT Organizer', 'Desktop notifications are now enabled! 🎉');
      }
    } else {
      toggleNotifications(true);
    }
  };

  const handleDisableNotifications = () => {
    toggleNotifications(false);
  };

  const handleTestNotification = async () => {
    // First check if we need to request permission
    if (permission !== 'granted') {
      console.log('[NotificationSettings] Requesting permission first...');
      const result = await requestPermission();
      if (result !== 'granted') {
        alert('❌ Please allow notifications in your browser to test them.\n\nClick "Enable Notifications" first, then try testing again.');
        return;
      }
    }
    
    if (!canNotify) {
      alert('❌ Please enable notifications first by clicking "Enable Notifications" button.');
      return;
    }
    
    try {
      console.log('[NotificationSettings] Sending test notification...');
      
      // Try using the browser Notification API directly for testing
      if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification('🔔 Test Notification', {
          body: 'This is what your notifications will look like!',
          icon: '/icon-192.svg',
          badge: '/icon-192.svg',
        });
        
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
        
        console.log('[NotificationSettings] Test notification sent successfully');
      } else {
        alert('❌ Notifications are not available. Please check your browser permissions.');
      }
    } catch (error) {
      console.error('Test notification failed:', error);
      alert('❌ Failed to send test notification.\n\nError: ' + (error as Error).message);
    }
  };
  
  const handleTestInAppNotification = async () => {
    try {
      console.log('[NotificationSettings] Sending test IN-APP notification...');
      await showNotification({
        type: 'info',
        title: '🔔 Test In-App Notification',
        message: 'This is what in-app notifications look like! They slide down from the top.',
        priority: 'high',
        userId: 'current',
        read: false,
        dismissed: false,
      });
      console.log('[NotificationSettings] In-app notification sent successfully');
    } catch (error) {
      console.error('In-app notification failed:', error);
      alert('❌ Failed to send in-app notification.\n\nError: ' + (error as Error).message);
    }
  };
  
  const handleShowAllNotifications = () => {
    // Navigate to a page that shows all notifications
    alert('📬 Your notifications appear at the bottom center of the screen when new ones arrive!\n\nClick "Test In-App Toast" to see one appear now.');
  };

  if (!isSupported) {
    return (
      <div className="p-6 rounded-xl bg-slate-800/30 border border-slate-700/50">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-white mb-1">Notifications Not Supported</h3>
            <p className="text-sm text-slate-300">
              Your browser doesn't support desktop notifications. Please try using a modern browser like Chrome, Firefox, or Edge.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-6 rounded-xl bg-gradient-to-br from-purple-900/30 via-slate-800/30 to-indigo-900/30 border border-purple-500/30">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {canNotify ? (
              <Bell className="w-6 h-6 text-purple-400" />
            ) : (
              <BellOff className="w-6 h-6 text-slate-400" />
            )}
            <div>
              <h3 className="text-lg font-semibold text-white">Desktop Notifications</h3>
              <p className="text-sm text-slate-300 mt-1">
                Get notified even when you're in other tabs or apps
              </p>
            </div>
          </div>
          
          {canNotify && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20 border border-green-500/50">
              <Check className="w-4 h-4 text-green-400" />
              <span className="text-sm font-medium text-green-300">Active</span>
            </div>
          )}
        </div>

        {/* Status & Actions */}
        <div className="space-y-3">
          {permission === 'denied' && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <p className="text-sm text-red-300">
                ❌ Notifications blocked. Please enable them in your browser settings.
              </p>
            </div>
          )}

          {permission === 'default' && (
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <p className="text-sm text-amber-300 mb-3">
                📬 Allow WHIPT to send you desktop notifications for:
              </p>
              <ul className="text-sm text-slate-300 space-y-1 ml-4 list-disc">
                <li>New messages from classmates</li>
                <li>Upcoming assignment deadlines</li>
                <li>Classes starting soon</li>
                <li>New assignments posted</li>
              </ul>
            </div>
          )}

          {permission === 'granted' && !isEnabled && (
            <div className="p-4 rounded-lg bg-slate-700/30 border border-slate-600/30">
              <p className="text-sm text-slate-300">
                Notifications are disabled. Enable them to stay updated.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            {!canNotify && (
              <button
                onClick={handleEnableNotifications}
                disabled={permission === 'denied'}
                className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {permission === 'denied' ? 'Blocked by Browser' : 'Enable Notifications'}
              </button>
            )}

            {canNotify && (
              <>
                <button
                  onClick={handleTestNotification}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-medium transition-colors"
                >
                  Test Browser Notification
                </button>
                <button
                  onClick={handleTestInAppNotification}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors"
                >
                  Test In-App Toast
                </button>
                <button
                  onClick={handleDisableNotifications}
                  className="px-4 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-medium transition-colors"
                >
                  Disable
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
        <p className="text-sm text-blue-300">
          💡 <strong>Tip:</strong> Desktop notifications work even when you're using other apps, as long as your browser is open. They won't work if you close the browser completely.
        </p>
      </div>
      
      {/* In-App Notifications Info */}
      <div className="p-6 rounded-xl bg-gradient-to-br from-purple-900/30 via-slate-800/30 to-indigo-900/30 border border-purple-500/30">
        <div className="flex items-start gap-3 mb-3">
          <Bell className="w-6 h-6 text-purple-400 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-white mb-1">In-App Toast Notifications</h3>
            <p className="text-sm text-slate-300">
              These appear at the <strong>bottom center</strong> of your screen when you receive notifications (messages, deadlines, etc.)
            </p>
          </div>
        </div>
        
        <button
          onClick={handleShowAllNotifications}
          className="w-full px-4 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors"
        >
          📬 Where Are My Notifications?
        </button>
      </div>
    </div>
  );
}
